import axios, { AxiosError } from 'axios';
import { BusinessActivity, CreateActivityInput, UpdateActivityInput, BusinessBooking } from '@/types/businessAppTypes';
import { BulkSlotCreate, BulkSlotUpdate } from '@/types/calendarManagementTypes';
import { CalendarApiResponse } from '@/types/calendarManagementTypes';
import { handleApiError } from './apiUtils';
import { apiClient } from '@/lib/apiClient';
import { Business } from '@/types/homepage';
import { ActivityForHomepage } from '@/types/homepage';

/**
 * MIGRATION NOTICE:
 * 
 * This file is being gradually migrated to use the consolidated API client from '@/lib/apiClient'.
 * New code should import and use apiClient directly.
 * 
 * Migration Plan:
 * 1. Add auth interceptor to existing api (DONE)
 * 2. Create consolidated apiClient in '@/lib/apiClient.ts' (DONE)
 * 3. Gradually migrate functions to use the new apiClient
 * 4. Once all functions are migrated, deprecate redundant API clients
 * 
 * Functions already migrated:
 * - getUnlockedSections
 * - updateUnlockedSections
 */

// Get API base URL from environment variables
const API_BASE_URL = import.meta.env.VITE_API_URL;

// Create API instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL || 'http://localhost:3000/api',
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Export the api instance for use in other files
export { api };

// Initialize API logging preference
export const initializeApiLogging = (enabled = false) => {
  // Only enable logging in development mode
  if (process.env.NODE_ENV === 'development') {
    localStorage.setItem('ENABLE_API_LOGGING', enabled ? 'true' : 'false');
    
    if (enabled) {
      console.log('🔄 API logging enabled. You will see detailed API request/response logs.');
    } else {
      console.log('🔄 API logging disabled. API logs will be minimal.');
    }
  }
};

// Default to disabled logging
if (process.env.NODE_ENV === 'development' && localStorage.getItem('ENABLE_API_LOGGING') === null) {
  localStorage.setItem('ENABLE_API_LOGGING', 'false');
}

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    // Generate a request ID for logging only
    const requestId = Date.now().toString(36) + Math.random().toString(36).substring(2, 5);
    
    // Only log if explicitly set by environment variable or localStorage
    const shouldLog = localStorage.getItem('ENABLE_API_LOGGING') === 'true';
    
    // Enhanced logging for development without adding the header
    if (process.env.NODE_ENV === 'development' && shouldLog) {
      console.log(`🔄 [API Request ${requestId}] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data
      });
    }
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add authentication interceptor to include token in requests
api.interceptors.request.use(
  (config) => {
    // Get the auth token from localStorage
    try {
      // First try businessAuth, then fallback to auth for backwards compatibility
      const businessAuth = JSON.parse(localStorage.getItem('businessAuth') || '{}');
      const publicAuth = JSON.parse(localStorage.getItem('auth') || '{}');
      const auth = businessAuth.token ? businessAuth : publicAuth;
      const token = auth.token;
      
      // 🔍 ENHANCED AUTH TOKEN DEBUGGING
      console.log('🔍 [API AUTH] Adding authentication to request:', {
        url: config.url,
        method: config.method,
        hasBusinessAuth: !!businessAuth.token,
        hasPublicAuth: !!publicAuth.token,
        usingAuth: businessAuth.token ? 'businessAuth' : (publicAuth.token ? 'publicAuth' : 'none'),
        tokenPreview: token ? token.substring(0, 20) + '...' : 'None',
        tokenLength: token ? token.length : 0,
        authData: {
          businessAuth: {
            hasToken: !!businessAuth.token,
            hasUser: !!businessAuth.user,
            userRole: businessAuth.user?.role || 'none'
          },
          publicAuth: {
            hasToken: !!publicAuth.token,
            hasUser: !!publicAuth.user,
            userRole: publicAuth.user?.role || 'none'
          }
        },
        timestamp: new Date().toISOString()
      });
      
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('✅ [API AUTH] Authorization header added to request');
      } else {
        console.warn('⚠️ [API AUTH] No token available for request');
      }
    } catch (error) {
      console.error('🚨 [API AUTH] Error adding auth token to request:', error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    // Generate a log ID for this response
    const logId = Date.now().toString(36);
    
    // Only log if explicitly set by environment variable or localStorage
    const shouldLog = localStorage.getItem('ENABLE_API_LOGGING') === 'true';
    
    // Enhanced logging for development
    if (process.env.NODE_ENV === 'development' && shouldLog) {
      console.log(`✅ [API Response ${logId}] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        data: response.data
      });
    }
    
    return response;
  },
  (error) => {
    // Log detailed error information
    if (error.response) {
      const logId = Date.now().toString(36);
      console.error(`❌ [API Error ${logId}] ${error.response.status} ${error.config.method?.toUpperCase()} ${error.config.url}`, {
        status: error.response.status,
        message: error.message,
        data: error.response.data,
        headers: error.response.headers
      });
    } else if (error.request) {
      console.error('❌ API Error - No Response:', { 
        request: error.request, 
        message: error.message 
      });
    } else {
      console.error('❌ API Setup Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Activity functions
export const getBusinessActivities = async (): Promise<BusinessActivity[]> => {
  try {
    const response = await api.get<BusinessActivity[]>('/business/activities');
    
    if (!response.data) {
      throw new Error('No data received from the server');
    }
    
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error fetching business activities:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      } else if (error.response?.status === 404) {
        throw new Error('Activities endpoint not found. Please check the API configuration.');
      } else if (!error.response) {
        throw new Error('Network error. Please check your connection and API configuration.');
      }
    }
    throw error;
  }
};

export const getBusinessDetails = async () => {
    try {
      const response = await api.get('/business/profile');
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error('Error fetching business details:', error.response?.data || error.message);
      }
      throw error;
    }
  };

export const createActivity = async (activityData: CreateActivityInput): Promise<BusinessActivity> => {
  try {
    const response = await api.post<BusinessActivity>('/business/activities', activityData);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error creating activity:', error.response?.data || error.message);
    }
    throw error;
  }
};

export const updateActivity = async (activityData: UpdateActivityInput): Promise<BusinessActivity> => {
  try {
    const response = await api.patch<BusinessActivity>(
      `/business/activities/${activityData.id}`,
      activityData
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error updating activity:', error.response?.data || error.message);
    }
    throw error;
  }
};

export const deleteActivity = async (activityId: string): Promise<void> => {
  try {
    await api.delete(`/business/activities/${activityId}`);
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error deleting activity:', error.response?.data || error.message);
    }
    throw error;
  }
};

//booking functions

export const getBusinessBookings = async (): Promise<BusinessBooking[]> => {
    try {
      // Try to get regular bookings first
      const response = await api.get<{ success: boolean; data: BusinessBooking[] }>('/business/bookings');
      
      console.log('📋 [Bookings API] Raw response:', {
        status: response.status,
        success: response.data?.success,
        dataType: typeof response.data?.data,
        dataLength: Array.isArray(response.data?.data) ? response.data.data.length : 'not array',
        fullResponse: response.data
      });
      
      // Handle different response structures
      if (response.data?.success === false) {
        console.warn('⚠️ [Bookings API] API returned success: false');
        return [];
      }
      
      if (!response.data?.data) {
        console.warn('⚠️ [Bookings API] No data field in response');
        return [];
      }
      
      if (!Array.isArray(response.data.data)) {
        console.warn('⚠️ [Bookings API] Data is not an array:', response.data.data);
        return [];
      }
      
      // If regular bookings are empty, try to get custom adventure bookings
      if (response.data.data.length === 0) {
        console.log('🔄 [Bookings API] No regular bookings found, trying custom adventure bookings...');
        try {
          const customBookings = await getConfirmedCustomAdventureBookings();
          if (customBookings.length > 0) {
            console.log(`✅ [Bookings API] Found ${customBookings.length} custom adventure bookings`);
            return customBookings;
          }
        } catch (customError) {
          console.error('❌ [Bookings API] Failed to fetch custom adventure bookings as fallback:', customError);
        }
      }
      
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error('❌ [Bookings API] Error fetching business bookings:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message
        });
        
        // If endpoint doesn't exist, try to get custom adventure bookings instead
        if (error.response?.status === 404) {
          console.log('🔄 [Bookings API] /business/bookings not found, trying custom adventure bookings...');
          try {
            return await getConfirmedCustomAdventureBookings();
          } catch (customError) {
            console.error('❌ [Bookings API] Failed to fetch custom adventure bookings as fallback:', customError);
            return [];
          }
        }
      }
      throw error;
    }
  };

// Helper function to get confirmed custom adventure bookings and transform them to BusinessBooking format
const getConfirmedCustomAdventureBookings = async (): Promise<BusinessBooking[]> => {
  try {
    const response = await getBusinessBookingRequests({ status: 'confirmed' });
    
    console.log('📋 [Custom Bookings] Found confirmed custom adventure bookings:', response.requests.length);
    
    // Transform custom adventure bookings to BusinessBooking format
    const transformedBookings: BusinessBooking[] = response.requests.map((request) => ({
      id: request.id,
      bookingNumber: request.requestNumber || `CA-${request.id.slice(-6)}`,
      customerName: request.customerName,
      activity: request.customAdventureName,
      date: new Date(request.requestedDateRange.startDate).toISOString(),
      participants: request.participants.totalCount,
      status: 'confirmed' as const,
      total: request.estimatedPrice.totalEstimate,
      currency: request.estimatedPrice.currency,
      paymentMethod: 'pending', // Custom adventures might not have payment method yet
      paymentStatus: 'pending' // Custom adventures might not have payment status yet
    }));
    
    return transformedBookings;
  } catch (error) {
    console.error('❌ [Custom Bookings] Error transforming custom adventure bookings:', error);
    return [];
  }
};


//calendar functions

export const getActivitiesCalendar = async (startDate: string, endDate: string): Promise<CalendarApiResponse> => {
    try {
      const response = await api.get<CalendarApiResponse>('/business/slots/calendar', {
        params: { startDate, endDate }
      });
      
      if (!response.data?.success) {
        throw new Error('Failed to fetch calendar events');
      }
      
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error('Error fetching calendar data:', error.response?.data || error.message);
      }
      throw error;
    }
  };
  
  
export const getUpcomingActivities = async () => {
try {
    const response = await api.get('/business/activities');
    return response.data;
} catch (error) {
    if (error instanceof AxiosError) {
    console.error('Error fetching upcoming activities:', error.response?.data || error.message);
    }
    throw error;
}
};


// calendar management functions

export const getSlotsCalendar = async (startDate: string, endDate: string) => {
    try {
      const response = await api.get('/business/slots/calendar-secondary', {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      handleApiError(error, 'Error fetching slots calendar');
      throw error;
    }
  };
  
  export const createBulkSlots = async (data: BulkSlotCreate) => {
    try {
      const response = await api.post('/business/slots/bulk-create', data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Error creating bulk slots');
      throw error;
    }
  };
  
  export const updateBulkSlots = async (data: BulkSlotUpdate) => {
    try {
      const response = await api.patch('/business/slots/bulk-update', data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Error updating bulk slots');
      throw error;
    }
  };
  
  export const deleteSlot = async (slotId: string) => {
    try {
      console.log('🗑️ API: Deleting time slot:', slotId);
      
      // Use the new time-slots endpoint that handles composite IDs
      const response = await api.delete(`/business/time-slots/${slotId}`);
      
      console.log('✅ API: Time slot deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ API: Error deleting time slot:', {
        slotId,
        error: error instanceof AxiosError ? {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        } : error
      });
      
      // Don't call handleApiError here to avoid hook issues
      if (error instanceof AxiosError) {
        console.error('Error deleting time slot:', error.response?.data || error.message);
      }
      throw error;
    }
  };
  
  export const getSlotsAvailability = async (activityId: string, startDate: string, endDate: string) => {
    try {
      const response = await api.get('/business/slots/availability', {
        params: { activityId, startDate, endDate }
      });
      return response.data;
    } catch (error) {
      handleApiError(error, 'Error fetching slots availability');
      throw error;
    }
  };

/**
 * Get unlocked sections for a business
 * @returns Object with feature flags
 */
export const getUnlockedSections = async () => {
  try {
    // Import the new apiClient
    const { apiClient } = await import('@/lib/apiClient');
    
    // Use the new client
    const response = await apiClient.get('/business/unlocked-sections');
    return response.data.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error fetching unlocked sections:', error.response?.data || error.message);
    }
    
    // Return empty object as fallback to prevent cascading errors
    return {};
  }
};

/**
 * Update unlocked sections for a business
 * @param sections Object with feature flags
 * @returns Updated object with feature flags
 */
export const updateUnlockedSections = async (sections: Record<string, boolean>) => {
  try {
    // Import the new apiClient
    const { apiClient } = await import('@/lib/apiClient');
    
    const response = await apiClient.put('/business/unlocked-sections', sections);
    return response.data.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error updating unlocked sections:', error.response?.data || error.message);
    }
    throw error;
  }
};

/**
 * Get completed setup steps for a business with caching
 * @returns Array of completed step keys
 */
export const getCompletedSetupSteps = async (): Promise<string[]> => {
  // Check localStorage cache first
  const cachedSteps = localStorage.getItem('completedSetupSteps');
  
  try {
    const { apiClient } = await import('@/lib/apiClient');
    const response = await apiClient.get('/business/completed-setup-steps');
    
    // Save to localStorage as cache
    const steps = response.data.data.steps || [];
    localStorage.setItem('completedSetupSteps', JSON.stringify(steps));
    
    return steps;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error fetching completed setup steps:', error.response?.data || error.message);
    }
    
    // Return cached data if available
    if (cachedSteps) {
      try {
        return JSON.parse(cachedSteps);
      } catch (e) {
        console.error('Error parsing cached steps:', e);
      }
    }
    
    // Return empty array as fallback
    return [];
  }
};

/**
 * Update completed setup steps for a business
 * @param steps Array of completed step keys
 * @returns Updated array of completed step keys
 */
export const updateCompletedSetupSteps = async (steps: string[]): Promise<string[]> => {
  // Update local cache immediately for fast UI updates
  localStorage.setItem('completedSetupSteps', JSON.stringify(steps));
  
  try {
    const { apiClient } = await import('@/lib/apiClient');
    const response = await apiClient.post('/business/completed-setup-steps', { steps });
    
    return response.data.data.steps || [];
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error updating completed setup steps:', error.response?.data || error.message);
    }
    
    // Return the steps we were trying to save as they're already in localStorage
    return steps;
  }
};

/**
 * Get dashboard stats for a business
 * @returns Dashboard stats object
 */
export const getDashboardStats = async () => {
  // Default stats as fallback
  const defaultStats = {
    totalBookings: 0,
    activeExperiences: 0,
    upcomingTours: 0,
    monthlyRevenue: 0
  };
  
  try {
    const { apiClient } = await import('@/lib/apiClient');
    const response = await apiClient.get('/business/dashboard-stats');
    
    return response.data.data || defaultStats;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error fetching dashboard stats:', error.response?.data || error.message);
    }
    
    return defaultStats;
  }
};

// Enhanced interface for individual dropdown choices with pricing
export interface DropdownChoice {
  value: string;
  pricing?: Array<{
    type: 'flat' | 'percentage' | 'per_unit' | 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
    value: number;
    description?: string;
    // Legacy: Multiplier selection for per_unit pricing (backward compatibility)
    multiplierType?: 'people' | 'days' | 'both';
  }>;
}

export interface OptionType {
  label: string;
  type: string;
  value?: any;
  required?: boolean;
  // Enhanced to support both simple strings (backward compatibility) and objects with pricing
  options?: string[] | DropdownChoice[];
  pricing?: Array<{
    type: 'flat' | 'percentage' | 'per_unit' | 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
    value: number;
    description?: string;
    // Legacy: Multiplier selection for per_unit pricing (backward compatibility)
    multiplierType?: 'people' | 'days' | 'both';
  }>;
  // Legacy: Multiplier functionality for dynamic pricing (backward compatibility)
  isMultiplier?: boolean;
  multiplierTarget?: string; // label of the option to multiply
  // NEW: Selection mode for dropdowns
  selectionMode?: 'single' | 'multiple';
}

// Custom Adventure Types
export interface CustomAdventure {
  id: string;
  businessId: string;
  name: string;
  description: string;
  options: Array<{
    label: string;
    type: string;
    required: boolean;
    // Enhanced to support both simple strings (backward compatibility) and objects with pricing
    options?: string[] | DropdownChoice[];
    pricing?: Array<{
      type: 'flat' | 'percentage' | 'per_unit' | 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
      value: number;
      description?: string;
      // Legacy: Multiplier selection for per_unit pricing (backward compatibility)
      multiplierType?: 'people' | 'days' | 'both';
    }>;
    // Legacy: Multiplier functionality for dynamic pricing (backward compatibility)
    isMultiplier?: boolean;
    multiplierTarget?: string; // label of the option to multiply
    // Selection mode for dropdowns
    selectionMode?: 'single' | 'multiple';
    // NEW: Enhanced UX properties for better business user experience
    helpText?: string; // Business-friendly explanation of what this field does
    placeholder?: string; // Example value to guide users
    isFoundationField?: boolean; // Mark critical pricing foundation fields
    businessCategory?: 'pricing-foundation' | 'add-ons' | 'upgrades' | 'requirements'; // Semantic grouping
  }>;
  basePrice: number;
  // NEW: Enhanced base pricing system
  basePriceType: 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
  currency: string;
  images?: string[];
  isActive: boolean;
  published: boolean;
  // NEW: Availability configuration
  availability?: {
    availabilityType: 'date_based' | 'always_available' | 'by_request';
    availableDates?: {
      date: string;
      isAvailable: boolean;
      maxBookings?: number;
      currentBookings: number;
      notes?: string;
    }[];
    defaultSettings?: {
      advanceBookingDays: number;
      maxBookingsPerDay: number;
      blockedWeekdays?: number[];
    };
  };
  createdAt?: string;
  updatedAt?: string;
  lastPublishedAt?: string;
}

export interface CustomAdventureInput {
  name: string;
  description: string;
  options: Array<{
    label: string;
    type: string;
    required: boolean;
    // Enhanced to support both simple strings (backward compatibility) and objects with pricing
    options?: string[] | DropdownChoice[];
    pricing?: Array<{
      type: 'flat' | 'percentage' | 'per_unit' | 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
      value: number;
      description?: string;
      // Legacy: Multiplier selection for per_unit pricing (backward compatibility)
      multiplierType?: 'people' | 'days' | 'both';
    }>;
    // Legacy: Multiplier functionality for dynamic pricing (backward compatibility)
    isMultiplier?: boolean;
    multiplierTarget?: string; // label of the option to multiply
    // Selection mode for dropdowns
    selectionMode?: 'single' | 'multiple';
    // NEW: Enhanced UX properties for better business user experience
    helpText?: string; // Business-friendly explanation of what this field does
    placeholder?: string; // Example value to guide users
    isFoundationField?: boolean; // Mark critical pricing foundation fields
    businessCategory?: 'pricing-foundation' | 'add-ons' | 'upgrades' | 'requirements'; // Semantic grouping
  }>;
  basePrice: number;
  // NEW: Enhanced base pricing system
  basePriceType: 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
  currency: string;
  images?: string[];
  isActive: boolean;
  published: boolean;
}

// Enhanced price calculation interfaces
export interface PriceCalculationOption {
  optionId: string;
  value: any;
  quantity?: number;
  selectedChoices?: string[]; // For multi-select dropdowns
}

export interface PriceBreakdownItem {
  label: string;
  amount: number;
  description?: string;
}

export interface PriceCalculationResult {
  basePrice: number;
  totalPrice: number;
  currency: string;
  breakdown: PriceBreakdownItem[];
}

// Custom Adventure API Functions
export const getCustomAdventures = async (): Promise<CustomAdventure[]> => {
  try {
    // Define a MongoDB response type that includes _id
    interface MongoDBResponse {
      success: boolean;
      data: (Omit<CustomAdventure, 'id'> & { _id: string })[];
    }
    
    const response = await api.get<MongoDBResponse>('/business/custom-adventures');
    
    console.log('[DEBUG] Raw response from /business/custom-adventures:', response.data);
    
    if (!response.data.success) {
      throw new Error('Failed to fetch custom adventures');
    }
    
    // Make sure MongoDB _id is mapped to id for frontend use
    const adventures = response.data.data.map(adventure => ({
      ...adventure,
      id: adventure._id // Map MongoDB _id to our frontend id
    }));
    
    console.log('[DEBUG] Processed adventures:', adventures);
    
    return adventures;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error fetching custom adventures:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
    }
    throw new Error('Failed to fetch custom adventures. Please try again.');
  }
};

export const createCustomAdventure = async (data: CustomAdventureInput): Promise<CustomAdventure> => {
  try {
    const response = await api.post<{ success: boolean; data: CustomAdventure }>('/business/custom-adventures', data);
    
    if (!response.data.success) {
      throw new Error('Failed to create custom adventure');
    }
    
    return response.data.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error creating custom adventure:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
    }
    throw new Error('Failed to create custom adventure. Please try again.');
  }
};

export const updateCustomAdventure = async (id: string, data: Partial<CustomAdventureInput>): Promise<CustomAdventure> => {
  if (!id) {
    console.error('🚨 [API ERROR] Cannot update adventure with undefined ID');
    throw new Error('Invalid adventure ID provided');
  }
  
  try {
    // Define a MongoDB response type that includes _id
    interface MongoDBResponse {
      success: boolean;
      data: (Omit<CustomAdventure, 'id'> & { _id: string });
      message?: string;
    }
    
    console.log(`🔄 [API] Updating adventure ${id} with data:`, {
      adventureId: id,
      updateData: data,
      endpoint: `/business/custom-adventures/${id}`,
      timestamp: new Date().toISOString()
    });
    
    const startTime = Date.now();
    const response = await api.patch<MongoDBResponse>(`/business/custom-adventures/${id}`, data);
    const endTime = Date.now();
    
    console.log('📈 [API] Response received:', {
      duration: `${endTime - startTime}ms`,
      status: response.status,
      statusText: response.statusText,
      responseHeaders: response.headers,
      responseData: response.data
    });
    
    // Validate response structure
    if (!response.data) {
      console.error('🚨 [API ERROR] No data in response:', response);
      throw new Error('Invalid response: No data received from server');
    }
    
    if (!response.data.success) {
      console.error('🚨 [API ERROR] Server returned success: false:', {
        response: response.data,
        message: response.data.message
      });
      throw new Error(response.data.message || 'Failed to update custom adventure');
    }
    
    if (!response.data.data) {
      console.error('🚨 [API ERROR] No adventure data in successful response:', response.data);
      throw new Error('Invalid response: No adventure data received');
    }
    
    // Validate that the adventure data has required fields
    const adventureData = response.data.data;
    if (!adventureData._id) {
      console.error('🚨 [API ERROR] Adventure data missing _id:', adventureData);
      throw new Error('Invalid response: Adventure data missing MongoDB _id');
    }
    
    // Map MongoDB _id to frontend id and ensure all required fields are present
    const { _id, ...restData } = adventureData;
    const adventure: CustomAdventure = {
      ...restData,
      id: _id, // Map MongoDB _id to our frontend id
      // Ensure required fields have defaults if missing
      isActive: restData.isActive ?? true,
      published: restData.published ?? false,
      options: restData.options || [],
      basePrice: restData.basePrice || 0,
      currency: restData.currency || 'USD',
      basePriceType: restData.basePriceType || 'fixed'
    };
    
    console.log('✅ [API] Adventure successfully updated and mapped:', {
      originalId: id,
      responseId: _id,
      mappedId: adventure.id,
      updatedFields: Object.keys(data),
      finalAdventure: adventure
    });
    
    // Validate the specific field that was updated
    if (data.published !== undefined && adventure.published !== data.published) {
      console.warn('⚠️ [API WARNING] Published status mismatch:', {
        requested: data.published,
        returned: adventure.published,
        adventure: adventure
      });
    }
    
    return adventure;
    
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('🚨 [API ERROR] Axios error updating custom adventure:', {
        adventureId: id,
        updateData: data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        responseHeaders: error.response?.headers,
        requestConfig: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        },
        message: error.message,
        stack: error.stack
      });
      
      // Provide specific error messages based on status codes
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      } else if (error.response?.status === 403) {
        throw new Error('Permission denied. You may not have access to modify this adventure.');
      } else if (error.response?.status === 404) {
        throw new Error('Custom adventure not found. It may have been deleted.');
      } else if (error.response?.status === 422) {
        const validationErrors = error.response.data?.errors || error.response.data?.message;
        throw new Error(`Validation error: ${validationErrors || 'Invalid data provided'}`);
      } else if (error.response?.status && error.response.status >= 500) {
        throw new Error('Server error. Please try again in a few moments.');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
    } else if (error instanceof Error) {
      console.error('🚨 [API ERROR] General error updating adventure:', {
        adventureId: id,
        updateData: data,
        errorMessage: error.message,
        errorStack: error.stack
      });
      
      // Re-throw our custom error messages
      if (error.message.includes('Invalid response') || error.message.includes('Invalid adventure ID')) {
        throw error;
      }
    }
    
    console.error('🚨 [API ERROR] Unknown error type:', {
      adventureId: id,
      updateData: data,
      error: error
    });
    
    throw new Error('Failed to update custom adventure. Please try again.');
  }
};

export const deleteCustomAdventure = async (id: string): Promise<void> => {
  try {
    const response = await api.delete<{ success: boolean }>(`/business/custom-adventures/${id}`);
    
    if (!response.data.success) {
      throw new Error('Failed to delete custom adventure');
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error deleting custom adventure:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
      if (error.response?.status === 404) {
        throw new Error('Custom adventure not found.');
      }
    }
    throw new Error('Failed to delete custom adventure. Please try again.');
  }
};

export const getPublicCustomAdventures = async (businessId?: string): Promise<CustomAdventure[]> => {
  if (!businessId) {
    console.warn('getPublicCustomAdventures called without businessId');
    throw new Error('Business ID is required to fetch custom adventures');
  }

  try {
    // Use the correct endpoint for fetching custom adventures
    const url = `/custom-adventures?businessId=${businessId}`;
    console.log(`📍 Fetching public adventures from: ${url}`);
    
    const response = await api.get<{ success: boolean; data: CustomAdventure[] }>(url);
    
    if (!response.data.success) {
      console.error('⚠️ API returned success: false for custom adventures', response.data);
      throw new Error('Failed to fetch public custom adventures');
    }
    
    const adventures = response.data.data.map((adventure: any) => ({
      ...adventure,
      id: adventure._id || adventure.id, // Handle both MongoDB _id and normalized id
      businessId: adventure.businessId || businessId // Ensure businessId is always set
    }));
    
    // Enhanced validation for each adventure
    const validAdventures = adventures.filter(adventure => {
      const isValid = Boolean(
        adventure && 
        adventure.id && 
        adventure.name && 
        adventure.options && 
        Array.isArray(adventure.options) &&
        adventure.basePrice !== undefined &&
        adventure.currency &&
        adventure.published === true &&
        adventure.isActive === true
      );
      
      if (!isValid) {
        console.warn('❌ Filtered out invalid adventure:', {
          id: adventure?.id,
          name: adventure?.name,
          hasOptions: Boolean(adventure?.options),
          hasPrice: adventure?.basePrice !== undefined,
          hasCurrency: Boolean(adventure?.currency),
          isPublished: adventure?.published,
          isActive: adventure?.isActive
        });
      }
      return isValid;
    });
    
    console.log(`✅ Successfully fetched ${validAdventures.length} valid public adventures`);
    return validAdventures;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('❌ Error fetching public custom adventures:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url
      });
      
      if (error.response?.status === 404) {
        throw new Error('No custom adventures found for this business.');
      }
      if (error.response?.status === 403) {
        throw new Error('Custom adventures are not available for this business.');
      }
    }
    throw new Error('Failed to fetch public custom adventures. Please try again.');
  }
};

export const calculateCustomAdventurePrice = async (id: string, options: PriceCalculationOption[]): Promise<PriceCalculationResult> => {
  try {
    const response = await api.post<{ success: boolean; data: PriceCalculationResult }>(
      `/business/custom-adventures/${id}/calculate-price`,
      { options }
    );
    
    if (!response.data.success) {
      throw new Error('Failed to calculate adventure price');
    }
    
    return response.data.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error calculating adventure price:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
      if (error.response?.status === 404) {
        throw new Error('Custom adventure not found.');
      }
    }
    throw new Error('Failed to calculate adventure price. Please try again.');
  }
};

export const calculatePublicCustomAdventurePrice = async (id: string, options: PriceCalculationOption[]): Promise<PriceCalculationResult> => {
  if (!id || id === 'undefined') {
    console.error('❌ Invalid adventure ID provided to price calculation function:', id);
    throw new Error('Adventure not found. Please reload the page and try again.');
  }

  // Clean up ID if it has any query parameters (from forced refresh)
  const cleanId = id.split('?')[0];

  console.log(`📊 Making API request to calculate price for adventure ${cleanId} with options:`, options);
  
  try {
    // Construct full endpoint URL to prevent path issues
    const endpoint = `/custom-adventures/${cleanId}/public-calculate-price`;
    console.log(`🔄 Using endpoint: ${endpoint}`);
    
    const response = await api.post<{ success: boolean; data: PriceCalculationResult }>(
      endpoint,
      { options }
    );
    
    if (!response.data.success) {
      console.error('⚠️ API returned success: false for price calculation', response.data);
      throw new Error('Failed to calculate adventure price');
    }
    
    console.log('✅ Price calculation successful:', response.data.data);
    return response.data.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('❌ Error calculating adventure price (public):', {
        status: error.response?.status,
        response_data: error.response?.data,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        params: error.config?.params,
        request_data: error.config?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error('Custom adventure not found.');
      }
      if (error.response?.status === 403) {
        throw new Error('This adventure is not available to the public.');
      }
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || 'Invalid request parameters.';
        throw new Error(errorMessage);
      }
    }
    throw new Error('Failed to calculate adventure price. Please try again.');
  }
};

// Custom Adventure Booking Request Types and Functions
export interface CustomAdventureBookingRequest {
  customAdventureId: string;
  businessId: string;
  requestedDateRange: {
    startDate: Date;
    endDate: Date;
    flexibility: 'exact' | 'flexible_1_week' | 'flexible_2_weeks' | 'flexible_1_month';
  };
  selectedOptions: Record<string, any>;
  estimatedPrice: {
    basePrice: number;
    optionsPrice: number;
    totalEstimate: number;
    currency: string;
  };
  participants: {
    adults: number;
    children: number;
    students: number;
  };
  customerDetails: {
    name: string;
    email: string;
    phone: string;
    specialRequests?: string;
    preferredContactMethod: 'email' | 'phone' | 'whatsapp';
  };
  metadata?: {
    source?: 'web' | 'mobile' | 'partner';
    deviceInfo?: string;
    referralSource?: string;
    campaignSource?: string;
  };
}

export interface CustomAdventureBookingResponse {
  requestNumber: string;
  status: string;
  estimatedPrice: {
    basePrice: number;
    optionsPrice: number;
    totalEstimate: number;
    currency: string;
  };
  requestedDateRange: {
    startDate: Date;
    endDate: Date;
    flexibility: string;
  };
  message: string;
}

export interface BookingRequestListItem {
  id: string;
  requestNumber: string;
  customAdventureName: string;
  customerName: string;
  customerEmail: string;
  requestedDateRange: {
    startDate: Date;
    endDate: Date;
    flexibility: string;
  };
  status: 'pending' | 'under_review' | 'accepted' | 'declined' | 'modified' | 'cancelled' | 'converted_to_booking';
  estimatedPrice: {
    totalEstimate: number;
    currency: string;
  };
  createdAt: Date;
  participants: {
    adults: number;
    children: number;
    students: number;
    totalCount: number;
  };
}

export interface BookingRequestDetails extends BookingRequestListItem {
  priceBreakdown?: Array<{ label: string; amount: number; description?: string }>;
  selectedOptions: Record<string, any>;
  customerDetails: {
    name: string;
    email: string;
    phone: string;
    specialRequests?: string;
    preferredContactMethod: 'email' | 'phone' | 'whatsapp';
  };
  businessResponse?: {
    respondedAt: Date;
    respondedBy: string;
    response: 'accept' | 'decline' | 'modify';
    message?: string;
    proposedModifications?: {
      dateRange?: {
        startDate: Date;
        endDate: Date;
      };
      pricing?: {
        modifiedPrice: number;
        reason: string;
      };
      options?: Record<string, any>;
    };
  };
  communication: {
    initialContactSent: boolean;
    remindersSent: number;
    lastReminderSent?: Date;
    messages: {
      timestamp: Date;
      type: 'email' | 'sms' | 'whatsapp' | 'system';
      direction: 'to_customer' | 'from_customer' | 'internal';
      content: string;
      sentBy?: string;
    }[];
  };
}

export interface BookingRequestStatistics {
  totalRequests: number;
  pendingRequests: number;
  thisMonthRequests: number;
  statusBreakdown: {
    _id: string;
    count: number;
    totalValue: number;
    avgResponseTime: number;
  }[];
  averageRequestValue: number;
}

/**
 * Submit a custom adventure booking request (Public endpoint)
 */
export const submitCustomAdventureBookingRequest = async (
  request: CustomAdventureBookingRequest
): Promise<CustomAdventureBookingResponse> => {
  try {
    console.log('📤 [API] Submitting custom adventure booking request:', {
      customAdventureId: request.customAdventureId,
      businessId: request.businessId,
      customerEmail: request.customerDetails.email,
      dateRange: request.requestedDateRange,
      estimatedPrice: request.estimatedPrice.totalEstimate,
      timestamp: new Date().toISOString()
    });

    const response = await api.post<{ 
      success: boolean; 
      data: CustomAdventureBookingResponse;
      message?: string;
    }>('/custom-adventure-bookings', request);

    if (!response.data.success) {
      console.error('🚨 [API ERROR] Server returned success: false:', response.data);
      throw new Error(response.data.message || 'Failed to submit booking request');
    }

    console.log('✅ [API] Booking request submitted successfully:', {
      requestNumber: response.data.data.requestNumber,
      status: response.data.data.status,
      totalEstimate: response.data.data.estimatedPrice.totalEstimate
    });

    return response.data.data;

  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('🚨 [API ERROR] Failed to submit booking request:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.error || 'Invalid booking request data';
        throw new Error(errorMessage);
      } else if (error.response?.status === 404) {
        throw new Error('Custom adventure not found or not available');
      } else if (error.response?.status && error.response.status >= 500) {
        throw new Error('Server error. Please try again in a few moments.');
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
    }

    console.error('🚨 [API ERROR] Unknown error submitting booking request:', error);
    throw new Error('Failed to submit booking request. Please try again.');
  }
};

/**
 * Get booking requests for the authenticated business
 */
export const getBusinessBookingRequests = async (
  filters?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    customAdventureId?: string;
    page?: number;
    limit?: number;
  }
): Promise<{
  requests: BookingRequestListItem[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  statistics: BookingRequestStatistics;
}> => {
  try {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.customAdventureId) params.append('customAdventureId', filters.customAdventureId);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    const url = `/custom-adventure-bookings/business${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<{ 
      success: boolean; 
      data: {
        requests: BookingRequestListItem[];
        pagination: {
          currentPage: number;
          totalPages: number;
          totalItems: number;
          itemsPerPage: number;
        };
        statistics: BookingRequestStatistics;
      };
    }>(url);

    if (!response.data.success) {
      throw new Error('Failed to fetch booking requests');
    }

    // Transform MongoDB _id to id for frontend compatibility
    const transformedData = {
      ...response.data.data,
      requests: response.data.data.requests.map((request: any) => ({
        ...request,
        id: request._id || request.id, // Use _id if available, fallback to id
      }))
    };

    return transformedData;

  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error fetching booking requests:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
    }
    throw new Error('Failed to fetch booking requests. Please try again.');
  }
};

/**
 * Get a specific booking request by ID
 */
export const getBookingRequestById = async (requestId: string): Promise<BookingRequestDetails> => {
  try {
    const response = await api.get<{ 
      success: boolean; 
      data: BookingRequestDetails;
    }>(`/custom-adventure-bookings/business/${requestId}`);

    if (!response.data.success) {
      throw new Error('Failed to fetch booking request details');
    }

    // Transform MongoDB _id to id for frontend compatibility
    const rawData = response.data.data as any; // Cast to any to access _id
    const transformedRequest: BookingRequestDetails = {
      ...rawData,
      id: rawData._id || rawData.id, // Use _id if available, fallback to id
    };

    return transformedRequest;

  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error fetching booking request details:', {
        requestId,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      } else if (error.response?.status === 404) {
        throw new Error('Booking request not found');
      }
    }
    throw new Error('Failed to fetch booking request details. Please try again.');
  }
};

/**
 * Respond to a booking request (accept/decline/modify)
 */
export const respondToBookingRequest = async (
  requestId: string,
  responseData: {
    response: 'accept' | 'decline' | 'modify';
    message?: string;
    proposedModifications?: {
      dateRange?: {
        startDate: Date;
        endDate: Date;
      };
      pricing?: {
        modifiedPrice: number;
        reason: string;
      };
      options?: Record<string, any>;
    };
  }
): Promise<BookingRequestDetails> => {
  try {
    const response = await api.post<{ 
      success: boolean; 
      data: BookingRequestDetails;
      message?: string;
    }>(`/custom-adventure-bookings/business/${requestId}/respond`, responseData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to respond to booking request');
    }

    return response.data.data;

  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error responding to booking request:', {
        requestId,
        responseData,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      } else if (error.response?.status === 404) {
        throw new Error('Booking request not found');
      } else if (error.response?.status === 400) {
        const errorMessage = error.response.data?.error || 'Invalid response data';
        throw new Error(errorMessage);
      }
    }
    throw new Error('Failed to respond to booking request. Please try again.');
  }
};

/**
 * Get booking request statistics for the authenticated business
 */
export const getBookingRequestStatistics = async (): Promise<BookingRequestStatistics> => {
  try {
    const response = await api.get<{ 
      success: boolean; 
      data: BookingRequestStatistics;
    }>('/custom-adventure-bookings/business/statistics');

    if (!response.data.success) {
      throw new Error('Failed to fetch booking request statistics');
    }

    return response.data.data;

  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error fetching booking request statistics:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
    }
    throw new Error('Failed to fetch booking request statistics. Please try again.');
  }
};

/**
 * Get confirmed custom adventure bookings for calendar display
 */
export const getCustomAdventureCalendarBookings = async (
  startDate: string, 
  endDate: string
): Promise<any[]> => {
  try {
    const response = await api.get<{ 
      success: boolean; 
      data: any[];
    }>('/custom-adventure-bookings/business/calendar', {
      params: { startDate, endDate }
    });

    if (!response.data.success) {
      throw new Error('Failed to fetch calendar bookings');
    }

    // console.log('📅 [Calendar] Custom adventure bookings loaded:', {
    //   count: response.data.data.length,
    //   dateRange: { startDate, endDate },
    //   bookings: response.data.data.map(b => ({ 
    //     id: b.id, 
    //     title: b.title, 
    //     start: b.start,
    //     type: b.extendedProps?.type 
    //   }))
    // });

    return response.data.data;

  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error fetching calendar bookings:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
    }
    
    // Return empty array instead of throwing to prevent calendar from breaking
    console.warn('⚠️ [Calendar] Failed to fetch custom adventure bookings, returning empty array');
    return [];
  }
};

/**
 * Get public status of a booking request by request number
 */
export const getPublicBookingRequestStatus = async (requestNumber: string): Promise<{
  requestNumber: string;
  status: string;
  estimatedPrice: {
    basePrice: number;
    optionsPrice: number;
    totalEstimate: number;
    currency: string;
  };
  requestedDateRange: {
    startDate: Date;
    endDate: Date;
    flexibility: string;
  };
  participants: {
    adults: number;
    children: number;
    students: number;
  };
  selectedOptions: Record<string, any>;
  customerDetails: {
    name: string;
    email: string;
    phone: string;
    specialRequests?: string;
  };
  customAdventure: {
    name: string;
    businessName: string;
    businessContact: {
      email: string;
      phone?: string;
    };
  };
  businessResponse?: {
    respondedAt: Date;
    response: 'accept' | 'decline' | 'modify';
    message?: string;
    proposedModifications?: any;
  };
  createdAt: Date;
  expiresAt?: Date;
}> => {
  try {
    const response = await api.get<{ 
      success: boolean; 
      data: any;
    }>(`/custom-adventure-bookings/public/${requestNumber}`);

    if (!response.data.success) {
      throw new Error('Failed to fetch booking request status');
    }

    return response.data.data;

     } catch (error) {
     if (error instanceof AxiosError && error.response) {
       console.error('Error fetching public booking request status:', {
         requestNumber,
         status: error.response.status,
         data: error.response.data,
         message: error.message
       });

       if (error.response.status === 404) {
         throw new Error('Booking request not found');
       }
     }
     throw new Error('Failed to fetch booking request status. Please try again.');
   }
};

// NEW: Date availability validation interfaces and functions
export interface DateAvailabilityResult {
  isAvailable: boolean;
  reason?: string;
  maxBookings?: number;
  currentBookings?: number;
  availableSpots?: number;
}

export interface DateRangeValidationResult {
  isValid: boolean;
  invalidDates: string[];
  reasons: string[];
  totalAvailableSpots: number;
}

/**
 * Validate date availability for a custom adventure
 */
export const validateCustomAdventureDateAvailability = async (
  adventureId: string,
  date: string // ISO date string (YYYY-MM-DD)
): Promise<DateAvailabilityResult> => {
  try {
    const response = await api.get<{ 
      success: boolean; 
      data: DateAvailabilityResult;
    }>(`/custom-adventures/public/${adventureId}/validate-date?date=${date}`);

    if (!response.data.success) {
      throw new Error('Failed to validate date availability');
    }

    return response.data.data;

  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error validating date availability:', {
        adventureId,
        date,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 404) {
        throw new Error('Adventure not found');
      }
    }
    throw new Error('Failed to validate date availability. Please try again.');
  }
};

/**
 * Validate date range availability for a custom adventure
 */
export const validateCustomAdventureDateRange = async (
  adventureId: string,
  startDate: string,
  endDate: string
): Promise<DateRangeValidationResult> => {
  try {
    const response = await api.get<{ 
      success: boolean; 
      data: DateRangeValidationResult;
    }>(`/custom-adventures/public/${adventureId}/validate-range?startDate=${startDate}&endDate=${endDate}`);

    if (!response.data.success) {
      throw new Error('Failed to validate date range availability');
    }

    return response.data.data;

  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error validating date range availability:', {
        adventureId,
        startDate,
        endDate,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 404) {
        throw new Error('Adventure not found');
      }
    }
    throw new Error('Failed to validate date range availability. Please try again.');
  }
};

/**
 * Fetches a business profile by ID
 * @param businessId - The ID of the business to fetch
 * @returns Promise<Business>
 */
export const fetchBusinessProfile = async (businessId: string): Promise<Business> => {
  try {
    const response = await apiClient.get(`/business/${businessId}/profile`);
    return response.data;
  } catch (error) {
    console.error('Error fetching business profile:', error);
    throw error;
  }
};

/**
 * Fetches all activities for a business
 * @param businessId - The ID of the business
 * @returns Promise<ActivityForHomepage[]>
 */
export const fetchBusinessActivities = async (businessId: string): Promise<ActivityForHomepage[]> => {
  try {
    const response = await apiClient.get(`/business/${businessId}/activities`);
    return response.data;
  } catch (error) {
    console.error('Error fetching business activities:', error);
    throw error;
  }
};

/**
 * Checks if a business has custom adventures available
 * @param businessId - The ID of the business to check
 * @returns Promise<{ hasCustomAdventures: boolean }>
 */
export const checkCustomAdventuresAvailability = async (businessId: string): Promise<{ hasCustomAdventures: boolean }> => {
  try {
    const response = await apiClient.get(`/business/${businessId}/custom-adventures/availability`);
    return response.data;
  } catch (error) {
    console.error('Error checking custom adventures availability:', error);
    throw error;
  }
};
